import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, ChevronLeft, Save } from 'lucide-react';
import { adminNoticeApi, NoticeFormData } from '@/api/noticeApi';

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'link', 'image',
];

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

export default function AdminNoticeForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const quillRef = useRef<ReactQuill | null>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isBanner, setIsBanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MAX_TITLE = 200;
  const MIN_CONTENT = 10;
  const MAX_CONTENT = 10000;
  const charCount = stripHtml(content).length;

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    adminNoticeApi.getDetail(Number(id))
      .then(res => {
        setTitle(res.data.title);
        setContent(res.data.content);
        setIsPinned(res.data.isPinned);
        setIsBanner(res.data.isBanner);
      })
      .catch(() => setError('공지사항을 불러오는데 실패했습니다.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const validate = (): string | null => {
    if (!title.trim()) return '제목을 입력해주세요.';
    if (title.trim().length > MAX_TITLE) return `제목은 ${MAX_TITLE}자 이하여야 합니다.`;
    const textContent = stripHtml(content);
    if (!textContent) return '내용을 입력해주세요.';
    if (textContent.length < MIN_CONTENT) return `내용은 ${MIN_CONTENT}자 이상 입력해주세요.`;
    if (textContent.length > MAX_CONTENT) return `내용은 ${MAX_CONTENT}자 이하여야 합니다.`;
    return null;
  };

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') return;
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        const index = range?.index ?? quill.getLength();
        quill.insertEmbed(index, 'image', reader.result, 'user');
        quill.setSelection({ index: index + 1, length: 0 });
      };
      reader.readAsDataURL(file);
    };
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: handleImageUpload,
      },
    },
  }), [handleImageUpload]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSubmitting(true);
    setError(null);
    const data: NoticeFormData = {
      title: title.trim(),
      content,
      isPinned,
      isBanner,
      isPublished: true,
    };
    try {
      if (isEdit) {
        await adminNoticeApi.update(Number(id), data);
      } else {
        await adminNoticeApi.create(data);
      }
      navigate('/dashboard/admin/notices');
    } catch {
      setError(`공지사항 ${isEdit ? '수정' : '등록'}에 실패했습니다.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-8 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold">{isEdit ? '공지사항 수정' : '공지사항 작성'}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {isEdit ? '공지사항 내용을 수정합니다.' : '새 공지사항을 작성합니다.'}
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">내용 입력</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* 제목 */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="title">제목 <span className="text-red-500">*</span></Label>
                <span className="text-xs text-gray-400">{title.length}/{MAX_TITLE}</span>
              </div>
              <Input
                id="title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={MAX_TITLE}
                placeholder="공지사항 제목을 입력해주세요."
                disabled={submitting}
              />
            </div>

            {/* 내용 */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>내용 <span className="text-red-500">*</span></Label>
                <span className={`text-xs ${charCount > MAX_CONTENT ? 'text-red-500' : 'text-gray-400'}`}>
                  {charCount.toLocaleString()}/{MAX_CONTENT.toLocaleString()}
                </span>
              </div>
              <div className={`rounded-md border border-input overflow-hidden ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={QUILL_FORMATS}
                  placeholder="공지사항 내용을 입력해주세요."
                  style={{ minHeight: '320px' }}
                />
              </div>
            </div>

            {/* 옵션 */}
            <div className="flex flex-wrap gap-6 pt-2 border-t">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={e => setIsPinned(e.target.checked)}
                  disabled={submitting}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">📌 상단 고정</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isBanner}
                  onChange={e => setIsBanner(e.target.checked)}
                  disabled={submitting}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">🔔 배너 표시</span>
              </label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/admin/notices')}
            disabled={submitting}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            취소
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isEdit ? '수정 중...' : '등록 중...'}</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />{isEdit ? '수정 완료' : '등록'}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
